import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { useLocalMode } from './LocalModeContext'
import toast from 'react-hot-toast'

/**
 * Context for managing sensitive folder session state.
 * 
 * Provides session management including authentication, token storage,
 * automatic expiration handling, and session restoration.
 */
const SensitiveSessionContext = createContext({
  // Session state
  sessionToken: null,
  isAuthenticated: false,
  expiresAt: null,
  timeRemaining: null,
  
  // Session actions
  authenticate: () => {},
  revoke: () => {},
  isSessionValid: () => false,
  
  // Loading states
  isLoading: false,
  isRestoring: false
})

/**
 * Hook to use the sensitive session context.
 * Must be used within a SensitiveSessionProvider.
 */
export const useSensitiveSession = () => {
  const context = useContext(SensitiveSessionContext)
  if (!context) {
    throw new Error('useSensitiveSession must be used within SensitiveSessionProvider')
  }
  return context
}

/**
 * Provider component for sensitive folder session management.
 * 
 * Features:
 * - Session token storage in sessionStorage (not localStorage for security)
 * - Automatic session expiration checking
 * - Session restoration on page reload
 * - Cleanup on user logout
 * - Toast notifications for session events
 */
export const SensitiveSessionProvider = ({ children }) => {
  const [sessionToken, setSessionToken] = useState(null)
  const [expiresAt, setExpiresAt] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRestoring, setIsRestoring] = useState(true)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [sessionPassword, setSessionPassword] = useState(null) // Temporarily store password for session

  const { user } = useAuth()
  const { isLocalMode } = useLocalMode()

  /**
   * Check if the current session is valid (not expired).
   */
  const isSessionValid = useCallback(() => {
    if (!sessionToken || !expiresAt) {
      console.log('🔐 SensitiveSession: isSessionValid - missing token or expiry:', { hasToken: !!sessionToken, hasExpiry: !!expiresAt })
      return false
    }
    
    // For frontend-only sessions, use simpler validation
    if (sessionToken.startsWith('frontend-')) {
      const now = new Date()
      const expiry = new Date(expiresAt)
      const isValid = now < expiry
      console.log('🔐 SensitiveSession: Frontend session validation:', { 
        now: now.toISOString(), 
        expiry: expiry.toISOString(), 
        isValid,
        timeDiff: expiry.getTime() - now.getTime()
      })
      return isValid
    }
    
    // For backend sessions, use the original validation
    const now = new Date()
    const expiry = new Date(expiresAt)
    const isValid = now < expiry
    console.log('🔐 SensitiveSession: Backend session validation:', { 
      now: now.toISOString(), 
      expiry: expiry.toISOString(), 
      isValid,
      timeDiff: expiry.getTime() - now.getTime()
    })
    return isValid
  }, [sessionToken, expiresAt])

  /**
   * Calculate time remaining in session.
   */
  const calculateTimeRemaining = useCallback(() => {
    if (!expiresAt) return null
    const remaining = new Date(expiresAt) - new Date()
    return remaining > 0 ? remaining : 0
  }, [expiresAt])

  /**
   * Authenticate for sensitive folder access.
   * 
   * @param {string} token - The session token
   * @param {string} expiry - The expiration timestamp
   * @param {string} password - The user's password (stored temporarily for session)
   */
  const authenticate = useCallback((token, expiry, password) => {
    console.log('🔐 SensitiveSession: Authenticating with token')
    console.log('🔐 SensitiveSession: Token received:', token)
    console.log('🔐 SensitiveSession: Expiry:', expiry)
    console.log('🔐 SensitiveSession: Expiry type:', typeof expiry)
    
    // Fix timezone issue: API returns timestamp that needs proper parsing
    let parsedExpiry
    if (typeof expiry === 'string') {
      // Handle microseconds in timestamp (e.g., .728075762 -> .728)
      const normalizedExpiry = expiry.replace(/\.(\d{3})\d+/, '.$1')
      
      // Try to parse the timestamp
      parsedExpiry = new Date(normalizedExpiry)
      
      // If the parsed time is invalid or significantly in the past,
      // it might be a timezone issue. Let's try a different approach.
      const now = new Date()
      const timeDiff = parsedExpiry.getTime() - now.getTime()
      
      // If the parsed time is more than 12 hours in the past, it's likely wrong
      if (timeDiff < -12 * 60 * 60 * 1000) {
        console.log('🔐 SensitiveSession: Detected timezone issue, trying alternative parsing...')
        
        // Try parsing as if it's in the current timezone
        // Split the timestamp and reconstruct it
        const parts = normalizedExpiry.split('T')
        if (parts.length === 2) {
          const datePart = parts[0]
          const timePart = parts[1]
          
          // Create a new date using the date and time parts
          const [year, month, day] = datePart.split('-')
          const [time, ms] = timePart.split('.')
          const [hour, minute, second] = time.split(':')
          
          // Create date in local timezone
          parsedExpiry = new Date(
            parseInt(year),
            parseInt(month) - 1, // Month is 0-indexed
            parseInt(day),
            parseInt(hour),
            parseInt(minute),
            parseInt(second),
            parseInt(ms || '0')
          )
        }
      }
    } else {
      parsedExpiry = new Date(expiry)
    }
    
    console.log('🔐 SensitiveSession: Expiry parsed:', parsedExpiry.toISOString())
    console.log('🔐 SensitiveSession: Current time:', new Date().toISOString())
    console.log('🔐 SensitiveSession: Time difference (ms):', parsedExpiry.getTime() - new Date().getTime())
    console.log('🔐 SensitiveSession: Setting authentication state...')
    
    setSessionToken(token)
    setExpiresAt(expiry)
    setIsAuthenticated(true)
    setSessionPassword(password) // Store password for session duration
    
    console.log('🔐 SensitiveSession: Authentication state updated')
    console.log('🔐 SensitiveSession: isAuthenticated should now be true')
    
    // Store in sessionStorage (not localStorage for security)
    sessionStorage.setItem('sensitiveToken', token)
    sessionStorage.setItem('sensitiveExpiry', expiry)
    sessionStorage.setItem('sensitivePassword', password) // Store for session duration
    
    toast.success('🔒 Sensitive folder unlocked', {
      duration: 3000,
      icon: '🔓'
    })
  }, [])

  /**
   * Revoke the current sensitive folder session.
   * 
   * @param {boolean} showToast - Whether to show a toast notification
   * @param {string} reason - Reason for revocation (for logging)
   */
  const revoke = useCallback(async (showToast = true, reason = 'manual') => {
    console.log(`🔐 SensitiveSession: Revoking session (${reason})`)
    
    // Try to revoke on server if we have a valid token
    if (sessionToken && isSessionValid()) {
      try {
        const { sensitiveAPI } = await import('../services/api')
        await sensitiveAPI.revokeAccess(sessionToken)
        console.log('🔐 SensitiveSession: Server session revoked successfully')
      } catch (error) {
        console.error('🔐 SensitiveSession: Failed to revoke server session:', error)
        // Continue with local cleanup even if server revocation fails
      }
    }
    
    // Clear local state
    setSessionToken(null)
    setExpiresAt(null)
    setIsAuthenticated(false)
    setTimeRemaining(null)
    setSessionPassword(null) // Clear stored password
    
    // Clear storage
    sessionStorage.removeItem('sensitiveToken')
    sessionStorage.removeItem('sensitiveExpiry')
    sessionStorage.removeItem('sensitivePassword')
    
    if (showToast) {
      const toastMessage = reason === 'expired' 
        ? '🔒 Sensitive folder session expired'
        : '🔒 Sensitive folder locked'
      
      toast(toastMessage, {
        duration: 3000,
        icon: '🔒'
      })
    }
  }, [sessionToken, isSessionValid])

  /**
   * Check session validity and handle expiration.
   */
  const checkSessionValidity = useCallback(() => {
    if (sessionToken && expiresAt) {
      if (!isSessionValid()) {
        console.log('🔐 SensitiveSession: Session expired, cleaning up')
        revoke(true, 'expired')
      } else {
        setTimeRemaining(calculateTimeRemaining())
      }
    }
  }, [sessionToken, expiresAt, isSessionValid, revoke, calculateTimeRemaining])

  /**
   * Restore session from storage on component mount.
   */
  useEffect(() => {
    console.log('🔐 SensitiveSession: Attempting to restore session from storage')
    
    const storedToken = sessionStorage.getItem('sensitiveToken')
    const storedExpiry = sessionStorage.getItem('sensitiveExpiry')
    const storedPassword = sessionStorage.getItem('sensitivePassword')
    
    if (storedToken && storedExpiry && storedPassword) {
      const expiryDate = new Date(storedExpiry)
      const now = new Date()
      
      if (now < expiryDate) {
        console.log('🔐 SensitiveSession: Restoring valid session')
        setSessionToken(storedToken)
        setExpiresAt(storedExpiry)
        setSessionPassword(storedPassword)
        setIsAuthenticated(true)
        setTimeRemaining(expiryDate - now)
      } else {
        console.log('🔐 SensitiveSession: Stored session expired, cleaning up')
        sessionStorage.removeItem('sensitiveToken')
        sessionStorage.removeItem('sensitiveExpiry')
        sessionStorage.removeItem('sensitivePassword')
      }
    } else {
      console.log('🔐 SensitiveSession: No stored session found')
    }
    
    setIsRestoring(false)
  }, [])

  /**
   * Set up session validity checking interval.
   */
  useEffect(() => {
    const interval = setInterval(checkSessionValidity, 30000) // Check every 30 seconds
    
    // Don't check immediately - wait for state to settle
    // This prevents false expiration detection during authentication
    const initialCheck = setTimeout(checkSessionValidity, 1000)
    
    return () => {
      clearInterval(interval)
      clearTimeout(initialCheck)
    }
  }, [checkSessionValidity])

  /**
   * Clean up session when user logs out.
   * Note: In local mode, user is null, so we should not clean up the session.
   */
  useEffect(() => {
    if (!user && isAuthenticated && !isLocalMode) {
      console.log('🔐 SensitiveSession: User logged out, cleaning up sensitive session')
      revoke(false, 'logout')
    }
  }, [user, isAuthenticated, isLocalMode, revoke])

  /**
   * Show warning when session is about to expire.
   */
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      // Show warning at 1 minute (60000ms) and 30 seconds (30000ms)
      if (timeRemaining <= 60000 && timeRemaining > 59000) {
        toast('⏰ Sensitive folder access expires in 1 minute', {
          duration: 4000,
          icon: '⚠️'
        })
      } else if (timeRemaining <= 30000 && timeRemaining > 29000) {
        toast('⏰ Sensitive folder access expires in 30 seconds', {
          duration: 4000,
          icon: '🚨'
        })
      }
    }
  }, [timeRemaining])

  /**
   * Update time remaining every second when session is active.
   */
  useEffect(() => {
    if (!isAuthenticated || !expiresAt) return

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining())
    }, 1000)

    return () => clearInterval(timer)
  }, [isAuthenticated, expiresAt, calculateTimeRemaining])

  const value = {
    // Session state
    sessionToken,
    isAuthenticated: isAuthenticated, // Don't double-check validity here
    expiresAt,
    timeRemaining,
    sessionPassword, // Temporarily stored password for API calls
    
    // Session actions
    authenticate,
    revoke,
    isSessionValid,
    
    // Loading states
    isLoading,
    isRestoring
  }

  return (
    <SensitiveSessionContext.Provider value={value}>
      {children}
    </SensitiveSessionContext.Provider>
  )
} 